import { useState } from 'react'
import { useQueryClient } from 'react-query'
import type { FileIssue } from '../../shared/rejection-messages'
import { runUpload } from '../../shared/run-upload'
import type { FileProgress } from '../../shared/run-upload'
import type { AvatarBoClient } from '../../shared/types'
import { uploadIdFor } from '../../shared/upload-plan'
import { avatarBoKeys } from './useAvatarCatalogue'

export interface UseOptionUploadOpts {
  client: AvatarBoClient
  setId?: string
  sectionId?: string
  onRegistered?: () => void
  stamp?: () => number
}

export function useOptionUpload({
  client,
  setId,
  sectionId,
  onRegistered,
  stamp = () => Date.now(),
}: UseOptionUploadOpts) {
  const qc = useQueryClient()
  const [uploading, setUploading] = useState(false)
  const [progress, setProgress] = useState<Record<string, FileProgress>>({})
  const [issues, setIssues] = useState<FileIssue[]>([])

  const start = async (input: {
    files: File[]
    displayName: string
    slug: string
    sortOrder?: number
  }) => {
    if (!setId || !sectionId) {
      return
    }
    setUploading(true)
    setIssues([])
    setProgress({})

    const outcome = await runUpload({
      client,
      files: input.files,
      uploadId: uploadIdFor(input.slug, stamp()),
      option: {
        setId,
        category: sectionId,
        slug: input.slug,
        displayName: input.displayName,
        ...(input.sortOrder === undefined ? {} : { sortOrder: input.sortOrder }),
      },
      onFile: (p) => setProgress((prev) => ({ ...prev, [p.name]: p })),
    })

    setUploading(false)

    if (!outcome.ok) {
      setIssues(outcome.issues)

      return
    }

    // Never optimistic: the option only exists once the server has accepted the folder.
    qc.invalidateQueries(avatarBoKeys.catalogue(setId))
    qc.invalidateQueries(avatarBoKeys.sets())
    onRegistered?.()
  }

  return {
    uploading,
    progress,
    issues,
    start,
    reset: () => {
      setIssues([])
      setProgress({})
    },
  }
}
